/**
 * The review step.
 *
 * Shows exactly what will be submitted, and — the part that matters — what will not.
 *
 * The omitted list is not padding. It is the visible proof of the invariant: questions the display
 * logic ruled out are listed as *not asked and not sent*, so a technician or a reviewer can see that
 * the form is not quietly shipping a default for them. Without this panel the guarantee is a claim
 * in a comment; with it, it is on screen.
 */

import { useMemo } from 'react';
import { renderAnswer } from './PrefillBadge.tsx';
import type { AnswerRecord, SubmissionPayload } from '../payload.ts';
import type { Problem } from '../validate.ts';
import type { FormSchema } from '../types.ts';

const PROVENANCE_LABEL: Record<AnswerRecord['provenance'], string> = {
  technician: 'You answered',
  prefill: 'From the record',
  override: 'You corrected the record',
  auto: 'Captured automatically',
};

export interface ReviewStepProps {
  readonly schema: FormSchema;
  readonly payload: SubmissionPayload;
  readonly problems: readonly Problem[];
  readonly onGoTo: (sectionId: string) => void;
  readonly onConfirmAll: () => void;
  readonly onSubmit: () => void;
  readonly submitting?: boolean;
}

export function ReviewStep(props: ReviewStepProps): JSX.Element {
  const { schema, payload, problems, onGoTo, onConfirmAll, onSubmit, submitting } = props;

  const sectionOf = useMemo(() => {
    const map = new Map<string, { id: string; title: string }>();
    for (const section of schema.sections) {
      for (const field of section.fields) map.set(field.id, { id: section.id, title: section.title });
    }
    return map;
  }, [schema]);

  const bySection = useMemo(() => {
    const groups = new Map<string, { title: string; answers: AnswerRecord[] }>();
    for (const answer of payload.answers) {
      const section = sectionOf.get(answer.fieldId) ?? { id: 'other', title: 'Other' };
      const group = groups.get(section.id) ?? { title: section.title, answers: [] };
      group.answers.push(answer);
      groups.set(section.id, group);
    }
    return [...groups.entries()];
  }, [sectionOf, payload.answers]);

  const hidden = payload.omitted.filter((o) => o.reason === 'hidden');
  const skipped = payload.omitted.filter((o) => o.reason === 'unanswered-optional');
  const labelOf = useMemo(() => {
    const map = new Map<string, string>();
    for (const section of schema.sections) for (const field of section.fields) map.set(field.id, field.label);
    return map;
  }, [schema]);

  /**
   * THE one confirmation surface (field feedback 2).
   *
   * Every record-sourced answer on the form, listed in a single place so the technician reads the
   * batch and flags only what is WRONG — instead of being asked "is this right?" once per field.
   * `prefill` provenance only: an `auto` value is the device's assertion, and an `override` is
   * already the technician's own corrected answer (it appears in the section listing with both
   * values). Per-field badges under each question are display-only; the taps happen here or not
   * at all — confirming never gates submission.
   */
  const prefilled = payload.answers.filter((a) => a.provenance === 'prefill');
  const unconfirmed = prefilled.filter((a) => a.confirmedAt === undefined);

  return (
    <section className="fx-review">
      <h2>Check before you send</h2>

      <dl className="fx-counts">
        <div>
          <dt>You answered</dt>
          <dd>{payload.counts.asked}</dd>
        </div>
        <div>
          <dt>From the record</dt>
          <dd>{payload.counts.prefilled}</dd>
        </div>
        <div>
          <dt>You corrected</dt>
          <dd>{payload.counts.overridden}</dd>
        </div>
        <div>
          <dt>Not asked of you</dt>
          <dd>{payload.counts.hiddenOmitted}</dd>
        </div>
        <div>
          <dt>Photos</dt>
          <dd>{payload.counts.photos}</dd>
        </div>
      </dl>

      {problems.length > 0 && (
        <div className="fx-blockers" role="alert">
          <h3>{problems.length} thing(s) still needed</h3>
          <ul>
            {problems.map((p) => (
              <li key={p.fieldId + p.kind}>
                <button type="button" className="fx-link" onClick={() => onGoTo(p.sectionId)}>
                  {p.sectionTitle} — {p.label}
                </button>
                : {p.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {prefilled.length > 0 && (
        <div className="fx-affirm">
          <h3>
            {prefilled.length} field{prefilled.length === 1 ? ' was' : 's were'} filled in from the record
          </h3>
          <p>
            Read the list. If one is wrong, tap Change and correct it — a correction is the most valuable thing you
            can record. Confirming is not required and never blocks sending.
          </p>
          <ul className="fx-affirm-list">
            {prefilled.map((answer) => (
              <li key={answer.fieldId} data-confirmed={answer.confirmedAt !== undefined ? 'yes' : 'no'}>
                <span className="fx-affirm-q">{answer.label}</span>
                <span className="fx-affirm-v">
                  {renderAnswer(answer.value)}
                  {answer.confirmedAt !== undefined && <span className="fx-prov-tag"> Confirmed</span>}
                </span>
                <button
                  type="button"
                  className="fx-link"
                  onClick={() => onGoTo(sectionOf.get(answer.fieldId)?.id ?? 'other')}
                >
                  Change
                </button>
              </li>
            ))}
          </ul>
          {unconfirmed.length > 0 ? (
            <button type="button" onClick={onConfirmAll}>
              These are all correct
            </button>
          ) : (
            <p className="fx-help">All confirmed — recorded with the submission.</p>
          )}
        </div>
      )}

      {bySection.map(([sectionId, group]) => (
        <div key={sectionId} className="fx-review-section">
          <h3>
            {group.title}{' '}
            <button type="button" className="fx-link" onClick={() => onGoTo(sectionId)}>
              Change
            </button>
          </h3>
          <dl>
            {group.answers.map((answer) => (
              <div key={answer.fieldId} data-provenance={answer.provenance}>
                <dt>{answer.label}</dt>
                <dd>
                  {renderAnswer(answer.value)}
                  <span className="fx-prov-tag"> {PROVENANCE_LABEL[answer.provenance]}</span>
                  {answer.provenance === 'override' && (
                    <span className="fx-prov-src"> (was {renderAnswer(answer.overrodeValue ?? null)})</span>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ))}

      <details className="fx-omitted">
        <summary>
          {hidden.length} question(s) did not apply and will not be sent
          {skipped.length > 0 && `, ${skipped.length} optional left blank`}
        </summary>
        <p className="fx-help">
          These are listed so you can see they are being left out rather than filled in for you. Nothing here reaches
          Salesforce.
        </p>
        <ul>
          {hidden.map((o) => (
            <li key={o.fieldId}>{labelOf.get(o.fieldId) ?? o.fieldId}</li>
          ))}
        </ul>
        {skipped.length > 0 && (
          <>
            <h4>Optional, left blank</h4>
            <ul>
              {skipped.map((o) => (
                <li key={o.fieldId}>{labelOf.get(o.fieldId) ?? o.fieldId}</li>
              ))}
            </ul>
          </>
        )}
      </details>

      {payload.unverifiedMappings.length > 0 && (
        <details className="fx-unverified">
          <summary>{payload.unverifiedMappings.length} answer(s) have no confirmed Salesforce field</summary>
          <p className="fx-help">
            These will be queued and stored, but the field they land in has not been verified against org metadata. They
            are flagged in the payload rather than written blind.
          </p>
          <ul>
            {payload.unverifiedMappings.map((m) => (
              <li key={m.fieldId}>
                {labelOf.get(m.fieldId) ?? m.fieldId} → {m.object}.{m.field}
              </li>
            ))}
          </ul>
        </details>
      )}

      <button type="button" className="fx-submit" onClick={onSubmit} disabled={problems.length > 0 || submitting === true}>
        {submitting === true ? 'Queueing…' : 'Submit'}
      </button>
      <p className="fx-help">
        Submitting queues this locally. It sends itself when there is a connection — you can close the app.
      </p>
    </section>
  );
}
